import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Clock, ShoppingBag, ArrowLeft, Copy, Check } from "lucide-react";
import { formatPrice } from "@/lib/cart";
import api from "@/lib/api";

const STATUS_CONFIG = {
  COMPLETED: { icon: CheckCircle2, color: "text-[hsl(142,42%,34%)]", bg: "bg-[hsl(142,42%,34%,0.08)]", label: "Payment Complete" },
  APPROVED: { icon: CheckCircle2, color: "text-[hsl(142,42%,34%)]", bg: "bg-[hsl(142,42%,34%,0.08)]", label: "Payment Approved" },
  PAYMENT_FAILED: { icon: XCircle, color: "text-[hsl(6,72%,46%)]", bg: "bg-[hsl(6,72%,46%,0.08)]", label: "Payment Failed" },
  FAILED: { icon: XCircle, color: "text-[hsl(6,72%,46%)]", bg: "bg-[hsl(6,72%,46%,0.08)]", label: "Payment Failed" },
  OPEN: { icon: Clock, color: "text-[hsl(38,92%,45%)]", bg: "bg-[hsl(38,92%,45%,0.08)]", label: "Awaiting Payment" },
  PAYMENT_PENDING: { icon: Clock, color: "text-[hsl(38,92%,45%)]", bg: "bg-[hsl(38,92%,45%,0.08)]", label: "Processing" },
};

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/shop/orders/${orderId}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setError("Order not found"))
      .finally(() => setLoading(false));
  }, [orderId]);

  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-4">
            <Skeleton className="h-16 w-16 rounded-full mx-auto" />
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !order) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <XCircle className="h-14 w-14 mx-auto text-[hsl(var(--destructive))] mb-4" />
          <h2 className="text-xl font-semibold">Order not found</h2>
          <Link to="/shop">
            <Button variant="outline" className="mt-4" data-testid="order-back-to-shop">Back to Shop</Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.OPEN;
  const StatusIcon = statusCfg.icon;
  const isSuccess = ["COMPLETED", "APPROVED"].includes(order.status);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Status Header */}
        <div className={`text-center py-8 rounded-xl mb-6 ${statusCfg.bg}`} data-testid="order-status-header">
          <StatusIcon className={`h-14 w-14 mx-auto mb-3 ${statusCfg.color}`} />
          <h1 className={`text-2xl font-semibold ${statusCfg.color}`}>
            {isSuccess ? "Thank you for your order!" : statusCfg.label}
          </h1>
          {isSuccess && (
            <p className="text-sm text-[hsl(var(--muted-fg))] mt-2">
              A confirmation will be sent to {order.guest_email || "your email"}
            </p>
          )}
        </div>

        {/* Order Details */}
        <Card className="border border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Order Details</CardTitle>
              <Badge
                className={`${isSuccess ? "bg-[hsl(142,42%,34%,0.15)] text-[hsl(142,42%,26%)] border border-[hsl(142,42%,34%,0.25)]" : "bg-[hsl(38,92%,45%,0.15)] text-[hsl(38,92%,30%)] border border-[hsl(38,92%,45%,0.25)]"}`}
                data-testid="order-status-badge"
              >
                {statusCfg.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Order ID */}
            <div className="flex items-center gap-2 mb-4 text-sm">
              <span className="text-[hsl(var(--muted-fg))]">Order ID:</span>
              <code className="font-mono text-xs bg-[hsl(var(--secondary))] px-2 py-1 rounded" data-testid="order-id-display">
                {orderId}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={copyOrderId}
                data-testid="order-copy-id"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>

            <Separator className="mb-4" />

            {/* Items */}
            <div className="space-y-3">
              {(order.line_items || []).map((item, i) => (
                <div key={i} className="flex justify-between gap-2 text-sm">
                  <span className="text-[hsl(var(--fg))]">
                    {item.name} x{item.quantity}
                  </span>
                  <span className="font-medium">
                    {item.total_money
                      ? formatPrice(item.total_money.amount, item.total_money.currency)
                      : "—"}
                  </span>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            {/* Total */}
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total Paid</span>
              <span className="text-xl font-bold" data-testid="order-total">
                {order.total_money
                  ? formatPrice(order.total_money.amount, order.total_money.currency)
                  : "—"}
              </span>
            </div>

            {order.created_at && (
              <p className="text-xs text-[hsl(var(--muted-fg))] mt-3">
                Placed on {new Date(order.created_at).toLocaleDateString("en-AU", {
                  day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Link to="/shop" className="flex-1">
            <Button
              variant="outline"
              className="w-full gap-2 min-h-[44px] border-[hsl(var(--border))]"
              data-testid="order-continue-shopping"
            >
              <ShoppingBag className="h-4 w-4" /> Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </PublicLayout>
  );
}
